/**
 * Director Carry-forward page — `/director/carry-forward`.
 *
 * The carry-forward feature (policy v1.0 §1.2) is NOT shipped in
 * production. HR handles the rare edge-cases offline via manual credit
 * grants at /hr/credits. This page exists as a navigation stub so that
 * the sidebar link and the e2e demo spec (`may13-item8-carry-forward-
 * director.spec.ts`) can navigate to the route without hitting a 404.
 *
 * When the carry-forward backend routes ship (tracked separately from
 * this slice) this page should be replaced with the real UI: a table of
 * HR-recommended carry-forward requests with Director approve/reject
 * actions (test-ids: `director-cf-card`, `director-cf-hr-note`,
 * `director-cf-reject`, `director-cf-decision-note`,
 * `director-cf-decide-save`).
 *
 * See: docs/domain-pitfalls — "No carry-forward feature"
 *      CLAUDE.md — "No carry-forward product feature"
 *      Memory: project_no_carry_forward_feature
 */
import { Card } from '../../components/primitives/Card'
import { PageHead } from '../../components/primitives/PageHead'
import { FadeIn } from '../../components/primitives/FadeIn'

/**
 * Stub page for `/director/carry-forward`.
 *
 * Renders an informational empty-state so navigation to this route
 * succeeds without a blank page or 404. Replace with real carry-forward
 * queue UI when the backend ships.
 */
export function DirectorCarryForwardPage(): JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Director"
        title="Carry-forward"
        subtitle="HR-recommended carry-forward requests escalated to you for a final decision."
      />
      <FadeIn>
        <Card padding={40} style={{ textAlign: 'center' }} data-testid="director-cf-empty-state">
          <div
            style={{
              fontSize: 40,
              marginBottom: 16,
              lineHeight: 1,
              opacity: 0.4,
            }}
            aria-hidden="true"
          >
            📋
          </div>
          <p
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--text)',
              margin: '0 0 8px',
            }}
          >
            No carry-forward requests
          </p>
          <p
            style={{
              fontSize: 13,
              color: 'var(--text-3)',
              margin: 0,
              lineHeight: 1.6,
              maxWidth: 360,
              marginInline: 'auto',
            }}
          >
            Carry-forward requests escalated by HR will appear here once the
            submission window opens. HR manages the current cycle offline via
            manual credit grants.
          </p>
        </Card>
      </FadeIn>
    </>
  )
}
